import { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, FormHelperText, Snackbar, Alert,
  Checkbox, CircularProgress, Chip, FormControlLabel,
  Backdrop, styled, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper
} from '@mui/material';
import { Close, Add, Delete, AccountBalance, Visibility, CheckCircle, Warning } from '@mui/icons-material';
import { fetchData } from '../../../apiService';
import { useMobileSpacing } from '../../../utils/mobileSpacing';

// Theme Configuration for Journal Entry styling
const themeConfig = {
  primary: '#324372',
  secondary: '#2a3a63',
  gradient: 'linear-gradient(135deg, #324372, #2a3a63)',
  light: 'rgba(50, 67, 114, 0.1)',
  background: 'rgba(50, 67, 114, 0.05)',
  headerBg: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)',
  dialogBg: 'linear-gradient(135deg, rgba(50, 67, 114, 0.05), rgba(42, 58, 99, 0.02))',
  // Lighter versions
  lighter: '#6b7db8',
  lightest: '#e8ebf5',
  softBackground: 'rgba(50, 67, 114, 0.03)',
  paleAccent: '#f0f2f8'
};

// Professional Dialog with modern business styling
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '8px',
    background: '#ffffff',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
    border: 'none',
    overflow: 'hidden',
    position: 'relative',
    // Mobile-specific optimizations
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
      margin: 0,
      maxHeight: '100vh',
      height: '100vh'
    }
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    [theme.breakpoints.down('sm')]: {
      backdropFilter: 'blur(2px)'
    }
  }
}));
// Professional TextField with compact styling
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    height: '28px', // Reduced from 32px to 28px
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d1d5db',
      borderWidth: '1px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#9ca3af',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: themeConfig.primary,
      borderWidth: '2px',
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dc6363',
      borderWidth: '2px',
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '12px', // Reduced from 13px to 12px
    color: '#6b7280',
    transform: 'translate(14px, 6px) scale(1)', // Adjusted for smaller height
    '&.Mui-focused': {
      color: themeConfig.primary,
      fontWeight: 600,
    },
    '&.Mui-error': {
      color: '#dc6363',
    },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)', // Adjusted for smaller height
    }
  },
  '& .MuiInputBase-input': {
    padding: '4px 8px', // Reduced padding from 6px 10px to 4px 8px
    fontSize: '12px', // Reduced from 13px to 12px
    fontWeight: 400,
    color: '#111827'
  },
  '& .MuiFormHelperText-root': {
    marginTop: '2px', // Reduced from 3px to 2px
    fontSize: '10px', // Reduced from 11px to 10px
    fontWeight: 400,
    '&.Mui-error': {
      color: '#dc6363',
      fontWeight: 500,
    }
  }
}));

// Professional FormControl with compact styling
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    height: '28px', // Reduced from 32px to 28px
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d1d5db',
      borderWidth: '1px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#9ca3af',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: themeConfig.primary,
      borderWidth: '2px',
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '12px', // Reduced from 13px to 12px
    color: '#6b7280',
    transform: 'translate(14px, 6px) scale(1)', // Adjusted for smaller height
    '&.Mui-focused': {
      color: themeConfig.primary,
      fontWeight: 600,
    },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)', // Adjusted for smaller height
    }
  },
  '& .MuiSelect-select': {
    padding: '4px 8px', // Reduced padding from 6px 10px to 4px 8px
    fontSize: '12px', // Reduced from 13px to 12px
    fontWeight: 400,
    color: '#111827'
  },
  '& .MuiMenuItem-root': {
    fontSize: '11px', // Smaller font for menu items
    padding: '6px 12px'
  }
}));

// Professional Button with compact styling
const StyledButton = styled(Button)(({ theme, variant }) => {
  if (variant === 'contained') {
    return {
      background: themeConfig.primary,
      color: 'white',
      fontWeight: 600,
      borderRadius: '4px',
      textTransform: 'none',
      boxShadow: 'none',
      border: 'none',
      minHeight: '32px',
      minWidth: '80px',
      fontSize: '13px',
      fontFamily: 'inherit',
      '&:hover': {
        background: themeConfig.secondary,
        boxShadow: 'none',
      },
      '&:disabled': {
        background: '#e5e7eb',
        color: '#9ca3af',
        boxShadow: 'none',
      }
    };
  }

  return {
    borderColor: '#d1d5db',
    color: '#374151',
    fontWeight: 600,
    borderRadius: '4px',
    textTransform: 'none',
    borderWidth: '1px',
    minHeight: '32px',
    minWidth: '80px',
    fontSize: '13px',
    fontFamily: 'inherit',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#9ca3af',
      backgroundColor: '#f9fafb',
      boxShadow: 'none',
    }
  };
});

// Professional Loading Overlay Component
const LoadingOverlay = ({ loading }) => (
  <Backdrop
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '12px'
    }}
    open={loading}
  >
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 3,
      textAlign: 'center'
    }}>
      <CircularProgress
        size={48}
        sx={{
          color: themeConfig.primary
        }}
      />

      <Box>
        <Typography
          variant="h6"
          sx={{
            color: themeConfig.primary,
            fontWeight: 600,
            mb: 1,
            fontSize: '18px'
          }}
        >
          Loading Journal Entry
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '14px'
          }}
        >
          Please wait while we prepare your form...
        </Typography>
      </Box>
    </Box>
  </Backdrop>
);
const JournalEntryModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  journalEntryDetails,
  setJournalEntryDetails,
  onSave,
  masters,
  gstRates,
  loading,
  companyId,
  token,
  fetchMaxBillNo,
  branchId,
  fromDate,
  toDate
}) => {
  const theme = useTheme();

  // Use the new mobile spacing utilities hook
  const mobileSpacing = useMobileSpacing();

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Cancel confirmation modal state
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Loading state for enhanced UX
  const [modalLoading, setModalLoading] = useState(false);

  // Radio button selection state
  const [headType, setHeadType] = useState('AccountHead'); // Default to AccountHead
  const [dynamicHeads, setDynamicHeads] = useState([]);
  const [loadingHeads, setLoadingHeads] = useState(false);

  // Child entry head types - track head type for each child entry
  const [childHeadTypes, setChildHeadTypes] = useState({});
  // Removed childDynamicHeads - we'll use cached data directly
  // Removed loadingChildHeads - no longer needed

  // Cache all data for performance - fetch once, use many times
  const [allVendors, setAllVendors] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [allBanks, setAllBanks] = useState([]);
  const [allAccountHeads, setAllAccountHeads] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const isViewMode = modalAction === 'view';

  // Helper function to convert MasterType number to string
  const getMasterTypeString = useCallback((masterType) => {
    console.log('getMasterTypeString called with:', masterType, 'type:', typeof masterType);
    
    // Convert to number if it's a string
    const numType = typeof masterType === 'string' ? parseInt(masterType, 10) : masterType;
    
    switch (numType) {
      case 1:
        console.log('Returning Vendor for type 1');
        return 'Vendor';
      case 2:
        console.log('Returning Customer for type 2');
        return 'Customer';
      case 3:
        console.log('Returning Bank for type 3');
        return 'Bank';
      case 4:
        console.log('Returning AccountHead for type 4');
        return 'AccountHead';
      default:
        console.log('Returning default AccountHead for type:', numType);
        return 'AccountHead'; // Default
    }
  }, []);

  // Helper function to convert MasterType string to number
  const getMasterTypeNumber = useCallback((masterTypeString) => {
    switch (masterTypeString) {
      case 'Vendor':
        return 1;
      case 'Customer':
        return 2;
      case 'Bank':
        return 3;
      case 'AccountHead':
        return 4;
      default:
        return 4; // Default to AccountHead
    }
  }, []);

  // Helper function to detect type from ID fields (fallback if MasterType is not available)
  const detectTypeFromIds = useCallback((data) => {
    if (data.VendorId) return 'Vendor';
    if (data.CustomerId) return 'Customer';
    if (data.BankId) return 'Bank';
    if (data.AccountHeadId) return 'AccountHead';
    return 'AccountHead'; // Default
  }, []);

  // Fetch all data once for performance optimization
  const fetchAllData = useCallback(async () => {
    if (dataLoaded) {
      console.log('Data already loaded, skipping fetch');
      return; // Don't fetch if already loaded
    }
    
    console.log('Fetching all data...');
    setLoadingHeads(true);
    try {
      // Fetch all data in parallel
      const [vendorsRes, customersRes, banksRes, accountHeadsRes] = await Promise.all([
        fetchData('VendorApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('CustomerApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('BankApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
        fetchData('AccountHeadApi', `/${companyId}`, token).catch(() => ({ response_data: [] }))
      ]);

      const vendors = vendorsRes.response_data || vendorsRes || [];
      const customers = customersRes.response_data || customersRes || [];
      const banks = banksRes.response_data || banksRes || [];
      const accountHeads = accountHeadsRes.response_data || accountHeadsRes || [];

      console.log('Fetched data:', {
        vendors: vendors.length,
        customers: customers.length,
        banks: banks.length,
        accountHeads: accountHeads.length
      });
      console.log('Bank data sample:', banks.slice(0, 2));
      console.log('Vendor data sample:', vendors.slice(0, 2));

      setAllVendors(vendors);
      setAllCustomers(customers);
      setAllBanks(banks);
      setAllAccountHeads(accountHeads);
      setDataLoaded(true);
      
      // Set initial dynamic heads based on default type
      setDynamicHeads(accountHeads);
    } catch (error) {
      console.error('Error fetching all data:', error);
      showError('Failed to fetch data');
    } finally {
      setLoadingHeads(false);
    }
  }, [companyId, token, dataLoaded]); // Removed fetchData from dependencies as it's imported

  // Get data based on selected type (from cache)
  const getDataByType = useCallback((type) => {
    let data = [];
    switch (type) {
      case 'Vendor':
        data = allVendors;
        break;
      case 'Customer':
        data = allCustomers;
        break;
      case 'Bank':
        data = allBanks;
        break;
      case 'AccountHead':
      default:
        data = allAccountHeads;
        break;
    }
    console.log(`getDataByType(${type}):`, data.length, 'items');
    return data;
  }, [allVendors, allCustomers, allBanks, allAccountHeads]);

  // Fetch dynamic heads based on selected type (now uses cached data)
  const fetchDynamicHeads = useCallback(async (type) => {
    if (!dataLoaded) {
      await fetchAllData();
    }
    setDynamicHeads(getDataByType(type));
  }, [dataLoaded, fetchAllData, getDataByType]);

  // Set head type based on formData when editing/viewing - RUNS FIRST
  useEffect(() => {
    // Only run for edit/view mode when modal is open
    if (!open || (modalAction !== 'edit' && modalAction !== 'view')) {
      return;
    }

    console.log('=== EDIT/VIEW USEEFFECT TRIGGERED ===');
    console.log('formData:', formData);
    
    // Set main head type if MasterId exists
    if (formData?.MasterId) {
      // Try to get type from MasterType field, fallback to detecting from ID fields
      let detectedType;
      if (formData.MasterType !== undefined && formData.MasterType !== null) {
        detectedType = getMasterTypeString(formData.MasterType);
        console.log('Main Head - Using MasterType:', formData.MasterType, '-> Type:', detectedType);
      } else {
        detectedType = detectTypeFromIds(formData);
        console.log('Main Head - Detected from IDs:', detectedType);
      }
      
      console.log('>>> SETTING HEAD TYPE TO:', detectedType);
      setHeadType(detectedType);
      
      // If data is not loaded yet, fetch it first
      if (!dataLoaded) {
        console.log('Data not loaded, fetching...');
        fetchAllData();
        return;
      }
      
      // Update main head dropdown data
      let newData = [];
      switch (detectedType) {
        case 'Vendor':
          newData = allVendors;
          break;
        case 'Customer':
          newData = allCustomers;
          break;
        case 'Bank':
          newData = allBanks;
          break;
        case 'AccountHead':
        default:
          newData = allAccountHeads;
          break;
      }
      console.log('Setting dynamicHeads:', newData.length, 'items for', detectedType);
      setDynamicHeads(newData);
    }
    
    // Set child head types
    if (journalEntryDetails && journalEntryDetails.length > 0) {
      const newChildTypes = {};
      journalEntryDetails.forEach((detail, index) => {
        let childType;
        const masterType = detail.MasterType || detail.masterType;
        if (masterType !== undefined && masterType !== null) {
          childType = getMasterTypeString(masterType);
        } else {
          childType = detectTypeFromIds(detail);
        }
        console.log(`Child ${index}: Type=${childType}, MasterType=${masterType}`);
        newChildTypes[index] = childType;
      });
      setChildHeadTypes(newChildTypes);
    }
  }, [open, modalAction, formData, journalEntryDetails, dataLoaded, allVendors, allCustomers, allBanks, allAccountHeads, getMasterTypeString, detectTypeFromIds, fetchAllData]);

  // Reset errors when modal opens/closes - RUNS SECOND
  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);
      
      // ONLY set default head type for CREATE mode
      // DO NOT touch headType for edit/view - it's handled by the other useEffect
      if (modalAction === 'create') {
        console.log('CREATE MODE: Setting default headType to AccountHead');
        setHeadType('AccountHead');
        
        // Fetch data if needed
        const loadData = async () => {
          if (!dataLoaded) {
            console.log('Fetching all data on modal open...');
            await fetchAllData();
          } else {
            console.log('Data already loaded, setting dynamicHeads to AccountHeads');
            setDynamicHeads(allAccountHeads);
          }
        };
        loadData();
      } else {
        // For edit/view, just ensure data is loaded
        if (!dataLoaded) {
          console.log('EDIT/VIEW MODE: Fetching data...');
          fetchAllData();
        }
      }

      // Reduce loading time for better UX
      setModalLoading(true);
      const timer = setTimeout(() => {
        setModalLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setModalLoading(false);
      setDynamicHeads([]);
      setChildHeadTypes({});
      // Reset headType when closing
      setHeadType('AccountHead');
    }
  }, [open, modalAction, dataLoaded, fetchAllData, allAccountHeads]);

  // Fetch heads when head type changes (uses cached data)
  useEffect(() => {
    if (open && headType) {
      console.log('HeadType changed to:', headType, 'modalAction:', modalAction);
      
      // If data is not loaded yet, fetch it first
      if (!dataLoaded) {
        console.log('Data not loaded, fetching...');
        fetchAllData().then(() => {
          console.log('Data loaded, now setting dynamicHeads');
        });
        return; // Exit early, will re-run when dataLoaded becomes true
      }
      
      // Get data directly based on type instead of using callback
      let newData = [];
      switch (headType) {
        case 'Vendor':
          newData = allVendors;
          break;
        case 'Customer':
          newData = allCustomers;
          break;
        case 'Bank':
          newData = allBanks;
          break;
        case 'AccountHead':
        default:
          newData = allAccountHeads;
          break;
      }
      
      console.log('Setting dynamicHeads with', newData.length, 'items for', headType);
      setDynamicHeads(newData);
      
      // Clear selected MasterId when changing head type (only if in create mode or if user manually changed the type)
      // Don't clear if we're in edit/view mode and this is the initial load
      if (modalAction === 'create') {
        setFormData(prev => ({ ...prev, MasterId: '' }));
      }
    }
  }, [headType, open, dataLoaded, modalAction, allVendors, allCustomers, allBanks, allAccountHeads, fetchAllData, setFormData]);

  // Calculate totals for child entries
  const calculateTotals = useCallback(() => {
    const totalChildAmount = journalEntryDetails.reduce((sum, detail) => sum + (parseFloat(detail.Amount) || 0), 0);

    setFormData(prev => ({
      ...prev,
      TotalChildAmount: totalChildAmount
    }));
  }, [journalEntryDetails, setFormData]);

  // Auto-calculate totals when details change
  useEffect(() => {
    if (journalEntryDetails.length > 0) {
      const timeoutId = setTimeout(() => {
        calculateTotals();
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [journalEntryDetails, calculateTotals]);

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle cancel with confirmation
  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };
  // Validation rules
  const validateField = (field, value) => {
    switch (field) {
      case 'BillNumber':
        if (!value || value.toString().trim() === '') {
          return 'Journal Entry No is required';
        }
        return '';
      case 'BillDate':
        if (!value) return 'Date is required';
        return '';
      case 'IsType':
        if (!value) return 'IsType is required';
        return '';
      case 'MasterId':
        if (!value) return 'Main Head is required';
        return '';
      case 'Amount':
        if (!value || parseFloat(value) <= 0) return 'Journal Entry Amount is required and must be greater than 0';
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    const billNumberError = validateField('BillNumber', formData.BillNumber);
    if (billNumberError) newErrors.BillNumber = billNumberError;

    const billDateError = validateField('BillDate', formData.BillDate);
    if (billDateError) newErrors.BillDate = billDateError;

    const isTypeError = validateField('IsType', formData.IsType);
    if (isTypeError) newErrors.IsType = isTypeError;

    const mainHeadError = validateField('MasterId', formData.MasterId);
    if (mainHeadError) newErrors.MasterId = mainHeadError;

    const journalAmountError = validateField('Amount', formData.Amount);
    if (journalAmountError) newErrors.Amount = journalAmountError;

    // Validate journal entry details
    if (journalEntryDetails.length === 0) {
      newErrors.journalEntryDetails = 'At least one child entry is required';
    } else {
      // Check if child amounts sum equals journal entry amount
      const totalChildAmount = journalEntryDetails.reduce((sum, detail) => sum + (parseFloat(detail.Amount) || 0), 0);
      const journalAmount = parseFloat(formData.Amount) || 0;
      const difference = Math.abs(totalChildAmount - journalAmount);

      // Use a small epsilon for floating point comparison (0.01 for currency)
      if (difference > 0.01) {
        newErrors.journalEntryDetails = `Total child amount (${totalChildAmount.toFixed(2)}) must equal Journal Entry Amount (${journalAmount.toFixed(2)})`;
      }

      // Validate each child entry
      journalEntryDetails.forEach((detail, index) => {
        if (!detail.MasterId) {
          newErrors.journalEntryDetails = `Child Head is required for entry ${index + 1}`;
        }
        // Removed validation: Child Head can now be the same as Main Head
        if (!detail.Amount || parseFloat(detail.Amount) <= 0) {
          newErrors.journalEntryDetails = `Amount is required and must be greater than 0 for entry ${index + 1}`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur for touched state
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Handle save with validation
  const handleSaveWithValidation = () => {
    // Mark all fields as touched
    setTouched({
      BillNumber: true,
      BillDate: true,
      IsType: true,
      MasterId: true,
      Amount: true,
      journalEntryDetails: true
    });

    // Build validation error messages
    const validationMessages = [];

    if (!formData.BillNumber || formData.BillNumber.toString().trim() === '') {
      validationMessages.push('Journal Entry No is required');
    }
    if (!formData.BillDate) {
      validationMessages.push('Date is required');
    }
    if (!formData.IsType) {
      validationMessages.push('IsType is required');
    }
    if (!formData.MasterId) {
      validationMessages.push('Main Head is required');
    }
    
    // Validate that only one ID field is set based on head type
    const idFieldsSet = [
      formData.CustomerId,
      formData.VendorId,
      formData.AccountHeadId,
      formData.BankId
    ].filter(id => id !== null && id !== undefined && id !== '').length;
    
    if (idFieldsSet === 0) {
      validationMessages.push(`${headType} ID must be set`);
    } else if (idFieldsSet > 1) {
      validationMessages.push('Only one ID field (Customer/Vendor/Bank/AccountHead) should be set');
    }
    
    // Validate the correct ID is set for the selected head type
    switch (headType) {
      case 'Customer':
        if (!formData.CustomerId) {
          validationMessages.push('Customer ID is required when Customer is selected');
        }
        if (formData.VendorId || formData.BankId || formData.AccountHeadId) {
          validationMessages.push('Only Customer ID should be set when Customer is selected');
        }
        break;
      case 'Vendor':
        if (!formData.VendorId) {
          validationMessages.push('Vendor ID is required when Vendor is selected');
        }
        if (formData.CustomerId || formData.BankId || formData.AccountHeadId) {
          validationMessages.push('Only Vendor ID should be set when Vendor is selected');
        }
        break;
      case 'Bank':
        if (!formData.BankId) {
          validationMessages.push('Bank ID is required when Bank is selected');
        }
        if (formData.CustomerId || formData.VendorId || formData.AccountHeadId) {
          validationMessages.push('Only Bank ID should be set when Bank is selected');
        }
        break;
      case 'AccountHead':
        if (!formData.AccountHeadId) {
          validationMessages.push('Account Head ID is required when Account Head is selected');
        }
        if (formData.CustomerId || formData.VendorId || formData.BankId) {
          validationMessages.push('Only Account Head ID should be set when Account Head is selected');
        }
        break;
      default:
        break;
    }
    
    if (!formData.Amount || parseFloat(formData.Amount) <= 0) {
      validationMessages.push('Journal Entry Amount is required and must be greater than 0');
    }
    if (journalEntryDetails.length === 0) {
      validationMessages.push('At least one child entry is required');
    } else {
      const totalChildAmount = journalEntryDetails.reduce((sum, detail) => sum + (parseFloat(detail.Amount) || 0), 0);
      const journalAmount = parseFloat(formData.Amount) || 0;
      const difference = Math.abs(totalChildAmount - journalAmount);

      // Use a small epsilon for floating point comparison (0.01 for currency)
      if (difference > 0.01) {
        validationMessages.push(`Total child amount (${totalChildAmount.toFixed(2)}) must equal Journal Entry Amount (${journalAmount.toFixed(2)})`);
      }

      // Validate each child entry
      journalEntryDetails.forEach((detail, index) => {
        const childType = childHeadTypes[index] || 'AccountHead';
        
        if (!detail.MasterId) {
          validationMessages.push(`Child Head is required for entry ${index + 1}`);
        }
        // Removed validation: Child Head can now be the same as Main Head
        if (!detail.Amount || parseFloat(detail.Amount) <= 0) {
          validationMessages.push(`Amount is required and must be greater than 0 for entry ${index + 1}`);
        }
        
        // Validate child entry IDs based on their individual head type
        switch (childType) {
          case 'Customer':
            if (!detail.CustomerId) {
              validationMessages.push(`Customer ID is required for child entry ${index + 1}`);
            }
            if (detail.VendorId || detail.BankId || detail.AccountHeadId) {
              validationMessages.push(`Only Customer ID should be set for child entry ${index + 1}`);
            }
            break;
          case 'Vendor':
            if (!detail.VendorId) {
              validationMessages.push(`Vendor ID is required for child entry ${index + 1}`);
            }
            if (detail.CustomerId || detail.BankId || detail.AccountHeadId) {
              validationMessages.push(`Only Vendor ID should be set for child entry ${index + 1}`);
            }
            break;
          case 'Bank':
            if (!detail.BankId) {
              validationMessages.push(`Bank ID is required for child entry ${index + 1}`);
            }
            if (detail.CustomerId || detail.VendorId || detail.AccountHeadId) {
              validationMessages.push(`Only Bank ID should be set for child entry ${index + 1}`);
            }
            break;
          case 'AccountHead':
            if (!detail.AccountHeadId) {
              validationMessages.push(`Account Head ID is required for child entry ${index + 1}`);
            }
            if (detail.CustomerId || detail.VendorId || detail.BankId) {
              validationMessages.push(`Only Account Head ID should be set for child entry ${index + 1}`);
            }
            break;
          default:
            break;
        }
      });
    }

    if (validationMessages.length > 0) {
      showError(validationMessages.join(' | '));
      validateForm();
      return;
    }

    if (validateForm()) {
      onSave();
    }
  };

  const handleChange = (field, value) => {
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    // If changing main head, set the appropriate ID field and MasterType based on head type
    if (field === 'MasterId' && value) {
      const updates = {
        MasterId: value,
        MasterType: getMasterTypeNumber(headType), // Set MasterType based on current headType
        CustomerId: null,
        VendorId: null,
        AccountHeadId: null,
        BankId: null
      };

      switch (headType) {
        case 'Customer':
          updates.CustomerId = value;
          break;
        case 'Vendor':
          updates.VendorId = value;
          break;
        case 'Bank':
          updates.BankId = value;
          break;
        case 'AccountHead':
        default:
          updates.AccountHeadId = value;
          break;
      }

      setFormData(prev => ({ ...prev, ...updates }));
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle detail changes
  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...journalEntryDetails];
    
    if (field === 'MasterId' && value) {
      const childType = childHeadTypes[index] || 'AccountHead';
      
      // Set the appropriate ID field and MasterType based on child's head type
      updatedDetails[index] = {
        ...updatedDetails[index],
        MasterId: value,
        MasterType: getMasterTypeNumber(childType), // Set MasterType based on child's headType
        CustomerId: null,
        VendorId: null,
        AccountHeadId: null,
        BankId: null
      };

      switch (childType) {
        case 'Customer':
          updatedDetails[index].CustomerId = value;
          break;
        case 'Vendor':
          updatedDetails[index].VendorId = value;
          break;
        case 'Bank':
          updatedDetails[index].BankId = value;
          break;
        case 'AccountHead':
        default:
          updatedDetails[index].AccountHeadId = value;
          break;
      }
    } else {
      updatedDetails[index] = {
        ...updatedDetails[index],
        [field]: value
      };
    }
    
    setJournalEntryDetails(updatedDetails);
    setHasUnsavedChanges(true);
  };

  // Handle child head type change
  const handleChildHeadTypeChange = async (index, type) => {
    // If data is not loaded yet, fetch it first and wait
    if (!dataLoaded) {
      console.log('Data not loaded, fetching before changing child head type...');
      await fetchAllData();
    }
    
    setChildHeadTypes(prev => ({ ...prev, [index]: type }));
    
    // Clear the selected MasterId and set MasterType when changing type
    const updatedDetails = [...journalEntryDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      MasterId: '',
      MasterType: getMasterTypeNumber(type), // Set MasterType based on new type
      CustomerId: null,
      VendorId: null,
      AccountHeadId: null,
      BankId: null
    };
    setJournalEntryDetails(updatedDetails);
    
    // No need to set child dynamic heads - we'll get data directly from cache using getDataByType
    
    setHasUnsavedChanges(true);
  };

  // Add new detail row
  const handleAddDetail = async () => {
    // If data is not loaded yet, fetch it first and wait
    if (!dataLoaded) {
      console.log('Data not loaded, fetching before adding child entry...');
      await fetchAllData();
    }
    
    const newIndex = journalEntryDetails.length;
    const newDetail = {
      Id: 0,
      MasterId: '',
      MasterType: 4, // Default to AccountHead (4)
      CustomerId: null,
      VendorId: null,
      AccountHeadId: null,
      BankId: null,
      Amount: 0,
      Remarks: ''
    };
    setJournalEntryDetails([...journalEntryDetails, newDetail]);
    
    // Initialize child head type to AccountHead by default
    setChildHeadTypes(prev => ({ ...prev, [newIndex]: 'AccountHead' }));
    
    // No need to set child dynamic heads - we'll get data directly from cache using getDataByType
    
    setHasUnsavedChanges(true);
  };

  // Remove detail row
  const handleRemoveDetail = (index) => {
    const updatedDetails = journalEntryDetails.filter((_, i) => i !== index);
    setJournalEntryDetails(updatedDetails);
    setHasUnsavedChanges(true);
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  return (
    <>
      <StyledDialog
        open={open}
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            if (hasUnsavedChanges && !isViewMode) {
              setCancelConfirmOpen(true);
              return;
            }
          }
          onClose();
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={mobileSpacing.isMobile}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: mobileSpacing.isMobile ? 0 : '8px',
            m: mobileSpacing.isMobile ? 0 : 1,
            maxWidth: '1000px',
            position: 'relative',
            overflow: 'hidden'
          }
        }}
      >

        {/* Loading Overlay */}
        <LoadingOverlay loading={modalLoading} />

        <DialogTitle sx={{
          py: 1.5,
          px: 2,
          background: themeConfig.primary,
          color: 'white',
          borderBottom: 'none'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '4px',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AccountBalance sx={{ color: 'white', fontSize: 16 }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'white',
                  fontSize: '16px',
                  fontFamily: 'inherit'
                }}
              >
                {modalAction === 'create' ? 'Create Journal Entry' :
                  modalAction === 'edit' ? 'Edit Journal Entry' : 'View Journal Entry'}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCancelClick}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{
          p: 1.5,
          background: '#f9fafb',
          minHeight: '300px'
        }}>
          <Box sx={{
            background: '#ffffff',
            borderRadius: '4px',
            p: 1.5,
            border: '1px solid #e5e7eb',
            boxShadow: 'none'
          }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: themeConfig.primary,
                mb: 1.5,
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontFamily: 'inherit'
              }}
            >
              Journal Entry Information
            </Typography>

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={1.9}>
                <StyledTextField
                  fullWidth
                  size="small"
                  label="Journal Entry No *"
                  value={formData.BillNumber || ''}
                  onChange={(e) => handleChange('BillNumber', e.target.value)}
                  onBlur={() => handleBlur('BillNumber')}
                  disabled={isViewMode}
                  error={touched.BillNumber && !!errors.BillNumber}
                  helperText={touched.BillNumber && errors.BillNumber}
                />
              </Grid>

              <Grid item xs={12} sm={1.9}>
                <StyledTextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Date *"
                  value={formData.BillDate || ''}
                  onChange={(e) => handleChange('BillDate', e.target.value)}
                  onBlur={() => handleBlur('BillDate')}
                  disabled={isViewMode}
                  error={touched.BillDate && !!errors.BillDate}
                  helperText={touched.BillDate && errors.BillDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={1.3}>
                <StyledFormControl fullWidth size="small">
                  <InputLabel>IsType *</InputLabel>
                  <Select
                    value={formData.IsType || ''}
                    onChange={(e) => handleChange('IsType', e.target.value)}
                    onBlur={() => handleBlur('IsType')}
                    disabled={isViewMode}
                    error={touched.IsType && !!errors.IsType}
                    label="IsType *"
                  >
                    <MenuItem value="Credit" sx={{ fontSize: '11px' }}>Credit</MenuItem>
                    <MenuItem value="Debit" sx={{ fontSize: '11px' }}>Debit</MenuItem>
                  </Select>
                  {touched.IsType && errors.IsType && (
                    <FormHelperText error>{errors.IsType}</FormHelperText>
                  )}
                </StyledFormControl>
              </Grid>

              <Grid item xs={12} sm={2.5}>
                <StyledTextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Journal Entry Amount *"
                  value={formData.Amount || ''}
                  onChange={(e) => handleChange('Amount', e.target.value)}
                  onBlur={() => handleBlur('Amount')}
                  disabled={isViewMode}
                  error={touched.Amount && !!errors.Amount}
                  helperText={touched.Amount && errors.Amount}
                  inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                />
              </Grid>

              <Grid item xs={12} sm={2.5}>
                {(() => {
                  const childSum = journalEntryDetails.reduce((sum, detail) => sum + (parseFloat(detail.Amount) || 0), 0);
                  const journalAmount = parseFloat(formData.Amount) || 0;
                  const difference = Math.abs(journalAmount - childSum);
                  const isZeroDifference = difference === 0.00;
                  const isValid = isZeroDifference; // Valid when difference is 0.00
                  
                  return (
                    <StyledTextField
                      fullWidth
                      size="small"
                      label="Child Sum"
                      value={formatCurrency(childSum)}
                      disabled
                      InputProps={{
                        readOnly: true,
                        style: { 
                          textAlign: 'right',
                          backgroundColor: isValid ? '#d1fae5' : '#fee2e2', // Green if valid, red if invalid
                          color: isValid ? '#065f46' : '#dc6363', // Dark green if valid, red if invalid
                          fontWeight: 600,
                          fontSize: '12px'
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: isValid ? '#10b981' : '#dc6363', // Green border if valid, red if invalid
                            borderWidth: '2px'
                          }
                        }
                      }}
                    />
                  );
                })()}
              </Grid>
            </Grid>

            {/* Main Head Type and Selection Row */}
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={5}>
                <Box>                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}>
                    {['Vendor', 'Customer', 'Bank', 'AccountHead'].map((type) => (
                      <FormControlLabel
                        key={type}
                        control={
                          <Checkbox
                            checked={headType === type}
                            onChange={() => {
                              setHeadType(type);
                              setHasUnsavedChanges(true);
                            }}
                            disabled={isViewMode}
                            size="small"
                            sx={{
                              padding: '6px',
                              '& .MuiSvgIcon-root': { fontSize: 18 },
                              color: '#9ca3af',
                              '&.Mui-checked': {
                                color: themeConfig.primary
                              }
                            }}
                          />
                        }
                        label={type}
                        sx={{
                          margin: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '13px',
                            fontWeight: headType === type ? 600 : 500,
                            color: headType === type ? themeConfig.primary : '#6b7280'
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={7}>
                <StyledFormControl fullWidth size="small">
                  <InputLabel>Main Head *</InputLabel>
                  <Select
                    value={formData.MasterId || ''}
                    onChange={(e) => handleChange('MasterId', e.target.value)}
                    onBlur={() => handleBlur('MasterId')}
                    disabled={isViewMode || loadingHeads}
                    error={touched.MasterId && !!errors.MasterId}
                    label="Main Head *"
                  >
                    <MenuItem value="" sx={{ fontSize: '11px' }}>
                      <em>{loadingHeads ? 'Loading...' : `Select ${headType}`}</em>
                    </MenuItem>
                    {dynamicHeads.map(head => (
                      <MenuItem key={head.Id} value={head.Id} sx={{ fontSize: '11px' }}>
                        {head.Name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.MasterId && errors.MasterId && (
                    <FormHelperText error>{errors.MasterId}</FormHelperText>
                  )}
                </StyledFormControl>
              </Grid>
            </Grid>

            {/* Remarks Row */}
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12}>                
                <StyledTextField
                  fullWidth
                  size="small"
                  label="Remarks"
                  value={formData.Remarks || ''}
                  onChange={(e) => handleChange('Remarks', e.target.value)}
                  disabled={isViewMode}
                  multiline
                  rows={2}                  
                  InputLabelProps={{
                    sx: {
                      fontSize: '13px',
                      fontWeight: 500
                    },
                    maxLength: 100
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      alignItems: 'flex-start',
                      padding: 0
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '13px',
                      fontWeight: 500,
                      lineHeight: 1.4,
                      padding: '8px 10px'
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      '& fieldset': {
                        borderColor: '#ccc'
                      }
                    }
                  }}
                />

              </Grid>
            </Grid>
            {/* Journal Entry Details Section */}
            <Box sx={{
              border: `1px solid #e0e0e0`,
              borderRadius: '8px',
              p: 1.5,
              backgroundColor: '#fafafa'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: '#333',
                    fontSize: '13px'
                  }}
                >
                  Child Entries (Multiple entries for selected main head)
                </Typography>
                {!isViewMode && (
                  <StyledButton
                    variant="contained"
                    size="small"
                    onClick={handleAddDetail}
                    startIcon={<Add />}
                    sx={{ fontSize: '12px', minWidth: '120px' }}
                  >
                    Add Child Entry
                  </StyledButton>
                )}
              </Box>

              {/* Details Table */}
              <TableContainer component={Paper} sx={{ maxHeight: 400, border: '1px solid #ddd' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '12px', backgroundColor: '#f5f5f5', minWidth: '180px' }}>
                        Type
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '12px', backgroundColor: '#f5f5f5', minWidth: '250px' }}>
                        Child Head
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '12px', backgroundColor: '#f5f5f5', textAlign: 'right', width: '150px' }}>
                        Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '12px', backgroundColor: '#f5f5f5', minWidth: '300px' }}>
                        Remarks
                      </TableCell>
                      {!isViewMode && (
                        <TableCell sx={{ fontWeight: 600, fontSize: '12px', backgroundColor: '#f5f5f5', textAlign: 'center', width: '80px' }}>
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {journalEntryDetails.map((detail, index) => {
                      const childType = childHeadTypes[index] || 'AccountHead';
                      const childHeads = getDataByType(childType); // Get data directly from cache
                      
                      // Debug logging
                      console.log(`Row ${index}: childType=${childType}, childHeads count=${childHeads?.length || 0}, dataLoaded=${dataLoaded}, allBanks=${allBanks.length}, allVendors=${allVendors.length}, allCustomers=${allCustomers.length}, allAccountHeads=${allAccountHeads.length}`);
                      
                      return (
                        <TableRow key={`${index}-${childType}-${dataLoaded}`}>
                          <TableCell sx={{ padding: '8px', verticalAlign: 'middle' }}>
                            {isViewMode ? (
                              <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                {childType}
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                {['Vendor', 'Customer', 'Bank', 'AccountHead'].map((type) => (
                                  <FormControlLabel
                                    key={type}
                                    control={
                                      <Checkbox
                                        checked={childType === type}
                                        onChange={() => handleChildHeadTypeChange(index, type)}
                                        disabled={isViewMode}
                                        size="small"
                                        sx={{
                                          padding: '2px',
                                          '& .MuiSvgIcon-root': { fontSize: 16 },
                                          color: '#9ca3af',
                                          '&.Mui-checked': {
                                            color: themeConfig.primary
                                          }
                                        }}
                                      />
                                    }
                                    label={type === 'AccountHead' ? 'AccHead' : type}
                                    sx={{
                                      margin: 0,
                                      '& .MuiFormControlLabel-label': {
                                        fontSize: '10px',
                                        fontWeight: childType === type ? 600 : 500,
                                        color: childType === type ? themeConfig.primary : '#6b7280'
                                      }
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ padding: '8px', verticalAlign: 'middle' }}>
                            {isViewMode ? (
                              <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                {childHeads.find(m => m.Id === detail.MasterId)?.Name || 
                                 dynamicHeads.find(m => m.Id === detail.MasterId)?.Name || 
                                 masters.find(m => m.Id === detail.MasterId)?.Name || '-'}
                              </Typography>
                            ) : (
                              <StyledFormControl fullWidth size="small">
                                <Select
                                  value={detail.MasterId || ''}
                                  onChange={(e) => handleDetailChange(index, 'MasterId', e.target.value)}
                                  displayEmpty
                                  disabled={!dataLoaded || loadingHeads}
                                  sx={{ fontSize: '12px' }}
                                  onOpen={() => {
                                    console.log(`Dropdown opened for row ${index}, childType=${childType}, childHeads available:`, childHeads);
                                  }}
                                >
                                  <MenuItem value="" sx={{ fontSize: '11px' }}>
                                    <em>{(!dataLoaded || loadingHeads) ? 'Loading...' : `Select ${childType}`}</em>
                                  </MenuItem>
                                  {dataLoaded && childHeads && childHeads.length > 0 ? (
                                    childHeads
                                      .map(head => (
                                        <MenuItem key={head.Id} value={head.Id} sx={{ fontSize: '11px' }}>
                                          {head.Name}
                                        </MenuItem>
                                      ))
                                  ) : (
                                    <MenuItem disabled sx={{ fontSize: '11px', color: 'red' }}>
                                      No {childType} data available (count: {childHeads?.length || 0})
                                    </MenuItem>
                                  )}
                                </Select>
                              </StyledFormControl>
                            )}
                          </TableCell>
                          <TableCell sx={{ padding: '8px', textAlign: 'right', verticalAlign: 'middle' }}>
                            {isViewMode ? (
                              <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                {formatCurrency(detail.Amount)}
                              </Typography>
                            ) : (
                              <StyledTextField
                                size="small"
                                type="number"
                                value={detail.Amount || ''}
                                onChange={(e) => handleDetailChange(index, 'Amount', e.target.value)}
                                inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', fontSize: '12px' } }}
                                fullWidth
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ padding: '8px', verticalAlign: 'middle' }}>
                            {isViewMode ? (
                              <Typography variant="body2" sx={{ fontSize: '12px' }}>
                                {detail.Remarks || '-'}
                              </Typography>
                            ) : (
                              <StyledTextField
                                size="small"
                                value={detail.Remarks || ''}
                                onChange={(e) => handleDetailChange(index, 'Remarks', e.target.value)}
                                fullWidth
                                inputProps={{ maxLength: 100 }}
                              />
                            )}
                          </TableCell>
                          {!isViewMode && (
                            <TableCell sx={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveDetail(index)}
                                sx={{ color: '#dc6363' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {journalEntryDetails.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isViewMode ? 4 : 5} sx={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          No child entries added
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals Display */}
              {journalEntryDetails.length > 0 && (
                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                    Total Child Amount: {formatCurrency(journalEntryDetails.reduce((sum, detail) => sum + (parseFloat(detail.Amount) || 0), 0))}
                  </Typography>
                  {(() => {
                    const difference = Math.abs((parseFloat(formData.Amount) || 0) - journalEntryDetails.reduce((sum, detail) => sum + (parseFloat(detail.Amount) || 0), 0));
                    const isZeroDifference = difference === 0.00;
                    const isValid = isZeroDifference; // Valid when difference is 0.00
                    
                    return (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: '13px',
                          color: isValid ? '#10b981' : '#dc6363', // Green if valid (0.00), red if invalid
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        Difference: {formatCurrency(difference)}
                        {isValid ? (
                          <Chip 
                            label="ALLOWED" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#10b981', 
                              color: 'white', 
                              fontSize: '10px',
                              height: '18px'
                            }} 
                          />
                        ) : (
                          <Chip 
                            label="NOT ALLOWED" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#dc6363', 
                              color: 'white', 
                              fontSize: '10px',
                              height: '18px'
                            }} 
                          />
                        )}
                      </Typography>
                    );
                  })()}
                </Box>
              )}

              {/* Validation Error for Details */}
              {touched.journalEntryDetails && errors.journalEntryDetails && (
                <Typography variant="caption" sx={{ color: '#dc6363', fontSize: '11px', mt: 1, display: 'block' }}>
                  {errors.journalEntryDetails}
                </Typography>
              )}
            </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{
          px: 1.5,
          py: 1,
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          gap: 1,
          justifyContent: 'flex-end'
        }}>
          <StyledButton
            onClick={handleCancelClick}
            variant="outlined"
            size="small"
          >
            Cancel
          </StyledButton>
          {!isViewMode && (
            <StyledButton
              onClick={handleSaveWithValidation}
              variant="contained"
              size="small"
              disabled={loading}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={14} sx={{ color: 'white' }} />
                  <span>Saving...</span>
                </Box>
              ) : (
                <>Save</>
              )}
            </StyledButton>
          )}
        </DialogActions>

      </StyledDialog>

      {/* Cancel Confirmation Dialog */}
      <StyledDialog
        open={cancelConfirmOpen}
        onClose={handleStayOnPage}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              backgroundColor: '#fef3c7',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
            }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ color: '#64748b', fontSize: '0.95rem', mb: 1 }}>
            You have unsaved changes that will be lost if you leave this page.
          </Typography>
          <Typography sx={{ color: '#dc6363', fontSize: '0.9rem', fontWeight: 600 }}>
            Are you sure you want to exit without saving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
          <StyledButton
            onClick={handleStayOnPage}
            variant="outlined"
            sx={{ minWidth: '120px' }}
          >
            Stay on Page
          </StyledButton>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{
              backgroundColor: '#dc6363',
              '&:hover': { backgroundColor: '#b91c1c' },
              minWidth: '120px',
              fontWeight: 600
            }}
          >
            Exit Without Saving
          </Button>
        </DialogActions>
      </StyledDialog>

      {/* Snackbar for errors */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            backdropFilter: 'blur(10px)',
            '& .MuiAlert-icon': {
              fontSize: '1.2rem'
            },
            '& .MuiAlert-message': {
              fontWeight: 500
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default JournalEntryModal;